import { ExternalLink } from 'lucide-react';
import { ReactNode } from 'react';

import { Firm } from '@/services/types/firms-types';

import { FirmFieldRow, FirmLayout, getFirmFieldsV2 } from './layout/FirmLayout';

export interface ViewFirmProps {
  firm: Firm;
  header?: ReactNode;
  footer?: ReactNode;
  inline?: boolean;
}

export const ViewFirm: React.FC<ViewFirmProps> = ({
  firm,
  header,
  footer,
  inline = false,
}) => {
  const fields = getFirmFieldsV2();

  const renderFieldValue = (
    field: ReturnType<typeof getFirmFieldsV2>[number],
    value: unknown,
  ) => {
    // Use custom transform if available
    if (field.transformValue) {
      return <div>{field.transformValue(value as never)}</div>;
    }

    // Handle URL fields
    if (field.type === 'url' && value && typeof value === 'string') {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          {value}
          <ExternalLink className="h-3 w-3" />
        </a>
      );
    }

    // Handle textarea fields (preserve line breaks)
    if (field.type === 'textarea' && value && typeof value === 'string') {
      return <div className="whitespace-pre-wrap">{value}</div>;
    }

    // Handle select fields with options - look up friendly label
    if (field.type === 'select' && field.options && value) {
      const option = field.options.find((o) => o.value === value);
      if (option) {
        return <div>{option.label}</div>;
      }
    }

    // Default text rendering
    return <div>{value?.toString() || '-'}</div>;
  };

  return (
    <FirmLayout header={header} footer={footer} inline={inline}>
      {fields.map((field) => {
        const value = firm[field.name];
        return (
          <FirmFieldRow key={field.name} label={field.label}>
            {renderFieldValue(field, value)}
          </FirmFieldRow>
        );
      })}
      {/* Contracted Cases / Month */}
      <FirmFieldRow label="Contracted Cases / Month" required={false}>
        <div>{firm.billing_config?.contracted_case_count ?? '-'}</div>
      </FirmFieldRow>

      {/* Current Front Channel */}
      <FirmFieldRow label="Front Channel" required={false}>
        <div>
          {firm.front_channel ? (
            <>
              {firm.front_channel?.name ||
                firm.front_channel?.front_id ||
                ''}
              {firm.front_channel?.address
                ? ` (${firm.front_channel.address})`
                : ''}
            </>
          ) : (
            '-'
          )}
        </div>
      </FirmFieldRow>

      {/* Fax Number */}
      <FirmFieldRow label="Fax Number" required={false}>
        <div>{firm.fax || '-'}</div>
      </FirmFieldRow>

      {/* EIN */}
      <FirmFieldRow label="EIN" required={false}>
        <div>{firm.ein || '-'}</div>
      </FirmFieldRow>

      {/* SMS Sender Number */}
      <FirmFieldRow label="SMS Sender Number" required={false}>
        <div>{firm.sms_sender_number || '-'}</div>
      </FirmFieldRow>

      {/* Firm Address Section */}
      <div className="py-4 border-t">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Firm Address
        </h3>
        {firm.address ? (
          <div className="space-y-1">
            <FirmFieldRow label="Address Line 1">
              <div>{firm.address.line1 || '-'}</div>
            </FirmFieldRow>
            {firm.address.line2 && (
              <FirmFieldRow label="Address Line 2">
                <div>{firm.address.line2}</div>
              </FirmFieldRow>
            )}
            <FirmFieldRow label="City">
              <div>{firm.address.city || '-'}</div>
            </FirmFieldRow>
            <FirmFieldRow label="State">
              <div>{firm.address.state || '-'}</div>
            </FirmFieldRow>
            <FirmFieldRow label="Postal Code">
              <div>{firm.address.postcode || '-'}</div>
            </FirmFieldRow>
            <FirmFieldRow label="Country">
              <div>{firm.address.country || '-'}</div>
            </FirmFieldRow>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No address set</div>
        )}
      </div>
    </FirmLayout>
  );
};
